import React, { useEffect, useState } from "react";
import axios from "axios";
import "./MyLocker.css";

const MyLocker = ({ platformUrl }) => {
    const [courses, setCourses] = useState([]);
    const [myGroups, setMyGroups] = useState([]);
    const [instructors, setInstructors] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [error, setError] = useState(null);

    const params = new URLSearchParams(window.location.search);
    const userUuid = params.get("uuid");
    const token = params.get("token");

    const formatPhoneNumber = (phone) => {
        const cleaned = ('' + phone).replace(/\D/g, '');
        if (cleaned.length === 10) {
            const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
            return match ? `(${match[1]}) ${match[2]}-${match[3]}` : phone;
        }
        return phone;
    };

    useEffect(() => {
        if (!token || !userUuid) {
            setError("Missing access token or user UUID. Please try again.");
            return;
        }

        const fetchData = async () => {
            try {
                const membershipsRes = await axios.get(
                    `${platformUrl}/learn/api/public/v1/users/uuid:${userUuid}/courses?role=Student`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                const courseMemberships = membershipsRes.data.results || [];

                const courseDetailsPromises = courseMemberships.map(m =>
                    axios.get(`${platformUrl}/learn/api/public/v3/courses/${m.courseId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    })
                );

                const courseDetails = await Promise.all(courseDetailsPromises);

                const formattedCourses = courseDetails.map(res => ({
                    name: res.data.name,
                    link: `${platformUrl}/ultra/courses/${res.data.id}/outline`
                }));

                setCourses(formattedCourses);

                const instructorMap = new Map();

                for (let membership of courseMemberships) {
                    const courseId = membership.courseId;
                    try {
                        const instructorsRes = await axios.get(
                            `${platformUrl}/learn/api/public/v1/courses/${courseId}/users?role=Instructor`,
                            { headers: { Authorization: `Bearer ${token}` } }
                        );

                        const instructorMemberships = instructorsRes.data.results || [];

                        for (const member of instructorMemberships) {
                            try {
                                const userRes = await axios.get(
                                    `${platformUrl}/learn/api/public/v1/users/${member.userId}`,
                                    { headers: { Authorization: `Bearer ${token}` } }
                                );

                                const userData = userRes.data;
                                const given = userData.name?.given || "";
                                const family = userData.name?.family || "";
                                const fullName = `${given} ${family}`.trim();
                                const email = userData.contact?.email || null;
                                const phone = userData.contact?.mobilePhone || null;

                                if (fullName && !instructorMap.has(fullName)) {
                                    instructorMap.set(fullName, { email, phone });
                                }
                            } catch (userErr) {
                                console.warn(`Failed to get user details for ${member.userId}:`, userErr.message);
                            }
                        }
                    } catch (instErr) {
                        console.warn(`Failed to fetch instructors for course ${courseId}:`, instErr.message);
                    }
                }

                setInstructors(Array.from(instructorMap.entries()));

                const groupResults = [];

                for (let membership of courseMemberships) {
                    const courseId = membership.courseId;
                    try {
                        const groupsRes = await axios.get(
                            `${platformUrl}/learn/api/public/v1/courses/${courseId}/groups`,
                            { headers: { Authorization: `Bearer ${token}` } }
                        );

                        const courseGroups = groupsRes.data.results || [];

                        for (let group of courseGroups) {
                            groupResults.push({
                                name: group.name
                            });
                        }
                    } catch (groupErr) {
                        console.warn(`Failed to fetch groups for course ${courseId}:`, groupErr.message);
                    }
                }

                setMyGroups(groupResults);

                // Fetch assignments and assessments
                const allAssignments = [];

                for (let membership of courseMemberships) {
                    const courseId = membership.courseId;
                    try {
                        const contentRes = await axios.get(
                            `${platformUrl}/learn/api/public/v1/courses/${courseId}/contents?recursive=true`,
                            { headers: { Authorization: `Bearer ${token}` } }
                        );

                        const allContent = contentRes.data.results || [];

                        const assignmentsInCourse = allContent.filter(
                            item =>
                                item.contentHandler?.id === "resource/x-bb-assignment" ||
                                item.contentHandler?.id === "resource/x-bb-asmt-test-link"
                        ).map(item => {
                            const altLink = item.links?.find(link => link.rel === "alternate")?.href;
                            return {
                                title: item.title,
                                link: altLink ? `${platformUrl}${altLink}` : `${platformUrl}/ultra/courses/${courseId}/outline/content/${item.id}`
                            };
                        });

                        allAssignments.push(...assignmentsInCourse);
                    } catch (err) {
                        console.warn(`Failed to fetch content for course ${courseId}:`, err.message);
                    }
                }

                setAssignments(allAssignments);

            } catch (err) {
                setError(err.message);
            }
        };

        fetchData();
    }, [platformUrl, token, userUuid]);

    return (
        <div className="bb-container locker">
            <h1 className="bb-title">My Locker</h1>
            {error && <div className="bb-error">Error: {error}</div>}
            <div className="locker-notes">
                <div className="sticky yellow">
                    <h3>My Homework</h3>
                    <ul>
                        {assignments.length === 0 ? (
                            <li>No assignments found</li>
                        ) : (
                            assignments.map((a, idx) => (
                                <li key={idx}>
                                    <a href={a.link} target="_blank" rel="noopener noreferrer">{a.title}</a>
                                </li>
                            ))
                        )}
                    </ul>
                </div>
                <div className="sticky blue">
                    <h3>My Courses</h3>
                    <ul>
                        {courses.map((course, idx) => (
                            <li key={idx}>
                                <a href={course.link} target="_blank" rel="noopener noreferrer">{course.name}</a>
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="sticky orange">
                    <h3>My Instructors</h3>
                    <ul>
                        {instructors.length === 0 ? (
                            <li>No instructors found</li>
                        ) : (
                            instructors.map(([name, contact], idx) => (
                                <li key={idx}>
                                    {name}
                                    {contact.email && (
                                        <div>
                                            <a href={`mailto:${contact.email}`}>{contact.email}</a>
                                        </div>
                                    )}
                                    {contact.phone && (
                                        <div>{formatPhoneNumber(contact.phone)}</div>
                                    )}
                                </li>
                            ))
                        )}
                    </ul>
                </div>
                <div className="sticky purple">
                    <h3>My Groups</h3>
                    {myGroups.length === 0 ? (
                        <p>No group memberships found.</p>
                    ) : (
                        <ul>
                            {myGroups.map((group, idx) => (
                                <li key={idx}>{group.name}</li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MyLocker;
